import { SVGProps, useMemo, useRef, useEffect } from "react";
import { SmartObjectSvgRendererProps } from "../";
import { useMetaVariableStore } from "../../../store";
import { getDynamicTextFontProperties, getSmartObjectMetaVariableKey } from "../../../utils";
import { useSmartObjectStyles } from "../useSmartObjectStyles";
import { handleMetaVariableClick } from "../handleMetaVariableClick";
import { useLessonPagesState } from "../../../../../contexts/LessonPagesProvider/LessonPagesProvider";
import styles from "../svg.module.css";
import { SmartObjectDynamicText } from "../../..";

const isMacBased = /iPad|Mac/.test(navigator.userAgent);
const osBasedLineHeight = isMacBased ? 0.85 : 1;

function moveCaretToEnd(quillElement: HTMLElement) {
  const selection = window.getSelection();
  const range = document.createRange();
  const lastNode = quillElement.childNodes[quillElement.childNodes.length - 1];

  if (quillElement.childNodes.length > 0) {
    if (lastNode.nodeType === Node.TEXT_NODE) {
      range.setStart(lastNode, lastNode.textContent?.length ?? 0);
    } else {
      range.setStartAfter(lastNode);
    }
  }

  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

export function SmartObjectSvgDynamicTextRenderer({
  element,
  objectId,
  parentId,
}: SmartObjectSvgRendererProps<SmartObjectDynamicText>) {
  if (element.type !== "text" && element?.children?.[0]) {
    const boundaryElement = element.children.find(
      (child: any) => child.type === "rect" && child["data-name"].includes("DYNAMIC_TEXT_BOUNDARY"),
    );

    const labelElement = element.children.find((child: any) => child.type === "text");

    const hasBoundary = !!boundaryElement;

    const newElement = {
      ...element,
      ...(hasBoundary ? boundaryElement : labelElement),
      hasBoundary,
    };

    return <SmartObjectSvgDynamicText element={newElement} objectId={objectId} parentId={parentId} />;
  }

  return <SmartObjectSvgDynamicText element={element} objectId={objectId} parentId={parentId} />;
}

export function SmartObjectSvgDynamicText({
  element,
  objectId,
  parentId,
}: SmartObjectSvgRendererProps<SmartObjectDynamicText>) {
  const { children, TYPE, GROUP_ID, VALUE, HOTSPOT_ID, RATE, type, hasBoundary, ...rest } = element;
  const key = getSmartObjectMetaVariableKey(objectId, GROUP_ID);
  const value = useMetaVariableStore((s) => s.metaVariables[key]);
  const data = useMetaVariableStore((s) => s.metaVariablesData[key]);
  const hide = useMetaVariableStore((s) => s.metaVariablesData[key]?.hide);

  const selectedKey = useMetaVariableStore((s) => s.selectedKey);
  const setSelectedKey = useMetaVariableStore((s) => s.setSelectedKey);
  const isSelected = selectedKey === key;

  const fontProperties = getDynamicTextFontProperties(data);

  const style = useSmartObjectStyles(element.style);

  const finalStyle = useMemo(() => {
    return {
      ...style,
      fontFamily: "inherit",
      fontSize: "inherit",
    };
  }, [style]);

  const textProps = rest as SVGProps<SVGForeignObjectElement>;
  const { selectedPanel } = useLessonPagesState();
  const [addNewMetaVariable, setMetaVariable, metaVariables] = useMetaVariableStore((s) => [
    s.addNewMetaVariable,
    s.setMetaVariable,
    s.metaVariables,
  ]);

  const containerRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSelectedKey(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setSelectedKey]);

  if (hide === false) {
    return null;
  }

  const extractTranslate = (transform: string | undefined) => {
    if (!transform) return { x: 0, y: 0 };

    const match = transform.match(/translate\(([-\d.]+)[ ,]([-\d.]+)\)/);
    return match ? { x: Number.parseFloat(match[1]), y: Number.parseFloat(match[2]) } : { x: 0, y: 0 };
  };

  const { x, y } = extractTranslate(textProps.transform);

  const handleSelect = () => {
    if (selectedPanel !== "advanced") return;

    handleMetaVariableClick({
      key,
      selectedPanel,
      addNewMetaVariable,
      setMetaVariable,
      metaVariables,
    });

    const elementToScroll = document.getElementById(`collapse-${key}`);
    const titleElement = document.getElementById(`title-tab-${key}`);

    if (elementToScroll) {
      elementToScroll.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      const floatingTextEditor = document.getElementById(`floating-text-editor-${key}`);
      if (floatingTextEditor) {
        const quill = floatingTextEditor.querySelector<HTMLElement>(".ql-editor");
        if (quill) {
          quill.focus();
          moveCaretToEnd(quill);
        }
      }

      if (titleElement) {
        titleElement.classList.remove(styles["highlight-fade"]);
        titleElement.classList.add(styles["highlight-fade"]);
        setTimeout(() => {
          titleElement.classList.remove(styles["highlight-fade"]);
        }, 1000);
      }
    }
  };

  return (
    <svg ref={containerRef} {...textProps} style={finalStyle} transform={undefined}>
      {hasBoundary && (
        <rect
          x={x}
          y={y}
          width={textProps.width ?? "100%"}
          height={textProps.height ?? "100%"}
          fill="none"
          stroke="gray"
          strokeDasharray="3"
          pointerEvents="none"
        />
      )}
      <foreignObject className="dynamic-text-foreign-object" x={x} y={y} width="100%" height="100%">
        <div
          style={{
            display: "inline-block",
            fontSize: fontProperties.fontSize,
            fontFamily: fontProperties.fontFamily,
            // TODO: Add other font properties later
            // BOLD, ITALIC, UNDERLINE, etc...
            color: "inherit",
            whiteSpace: "nowrap",
            cursor: "text",
            lineHeight: osBasedLineHeight,
          }}
          dangerouslySetInnerHTML={{ __html: value || "" }}
        />
      </foreignObject>
      {hasBoundary && (
        <rect
          x={x}
          y={y}
          width={textProps.width ?? "100%"}
          height={textProps.height ?? "100%"}
          fill="transparent"
          stroke={isSelected ? "#74C043" : "gray"}
          strokeWidth={isSelected ? 2 : 1}
          strokeDasharray={isSelected ? "0" : "3"}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedKey(key);
            handleSelect();
          }}
          style={{ cursor: "pointer" }}
        />
      )}
    </svg>
  );
}
